// Client-side mirror of utils/examMarkingGuide.js (backend). Keeps a theory
// question's free-text `marking_guide` (e.g. "1. Definition (2 marks) 2.
// Example (3 marks)") summing exactly to the question's score, live in the
// exam builder UI — instead of only reconciling on the backend at save time
// (which meant AI-generated guides looked wrong until the exam was saved and
// reloaded). Skips questions flagged `markingGuideEdited` (manually edited by
// the teacher/admin) so we never clobber intentional guide text.

const MARK_PATTERN = /(\d+(?:\.\d+)?)\s*(marks?|pts?|points?)\b/gi;

function extractMarks(text) {
  if (!text || typeof text !== 'string') return [];
  const matches = [...text.matchAll(MARK_PATTERN)];
  return matches.map(m => ({ value: parseFloat(m[1]), index: m.index, full: m[0], unit: m[2] }));
}

function reconcileMarkingGuide(text, targetScore) {
  if (!text || typeof text !== 'string') return text;
  if (typeof targetScore !== 'number' || !isFinite(targetScore) || targetScore <= 0) return text;

  const marks = extractMarks(text);
  if (marks.length === 0) return text;

  const currentTotal = marks.reduce((sum, m) => sum + m.value, 0);
  if (currentTotal <= 0) return text;
  if (Math.abs(currentTotal - targetScore) < 0.01) return text;

  const scale = targetScore / currentTotal;
  const scaled = marks.map(m => Math.round(m.value * scale * 2) / 2);

  const sumScaled = scaled.reduce((a, b) => a + b, 0);
  const drift = Math.round((targetScore - sumScaled) * 2) / 2;
  if (drift !== 0) {
    const lastIdx = scaled.length - 1;
    scaled[lastIdx] = Math.max(0, scaled[lastIdx] + drift);
  }

  let result = text;
  for (let i = marks.length - 1; i >= 0; i--) {
    const m = marks[i];
    const newValue = scaled[i];
    const formattedValue = Number.isInteger(newValue) ? String(newValue) : newValue.toFixed(1);
    const unitWord = /^marks?$/i.test(m.unit) ? (newValue === 1 ? 'mark' : 'marks')
      : /^pts?$/i.test(m.unit) ? m.unit
      : (newValue === 1 ? 'point' : 'points');
    const replacement = `${formattedValue} ${unitWord}`;
    result = result.slice(0, m.index) + replacement + result.slice(m.index + m.full.length);
  }
  return result;
}

function ensureMarkingGuideMarks(text, defaultTotal = 5) {
  if (!text || typeof text !== 'string' || extractMarks(text).length > 0) return text;

  const lines = text
    .replace(/\s+(?=\d+[.)]\s)/g, '\n')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);
  if (!lines.length) return text;

  const total = Math.max(defaultTotal, lines.length);
  const base = Math.floor((total / lines.length) * 2) / 2;
  const allocations = lines.map(() => base);
  const drift = Math.round((total - allocations.reduce((sum, value) => sum + value, 0)) * 2) / 2;
  allocations[allocations.length - 1] = Math.max(0.5, allocations[allocations.length - 1] + drift);

  return lines.map((line, index) => {
    const value = allocations[index];
    const formatted = Number.isInteger(value) ? String(value) : value.toFixed(1);
    return `${line} (${formatted} ${value === 1 ? 'mark' : 'marks'})`;
  }).join('\n');
}

/**
 * Reconciles marking_guide for every theory question in an array, using
 * getScoreForIndex(i) to determine each question's target score (so callers
 * can account for "equal marks" auto-distribution before it's written into
 * q.score). Returns the same array reference if nothing changed.
 */
function reconcileQuestionsForDisplay(questions, getScoreForIndex) {
  let changed = false;
  const next = (questions || []).map((q, i) => {
    if (q && q.questionType === 'theory' && q.marking_guide && !q.markingGuideEdited) {
      const target = Number(getScoreForIndex(i, q));
      if (target > 0) {
        const reconciled = reconcileMarkingGuide(q.marking_guide, target);
        if (reconciled !== q.marking_guide) { changed = true; return { ...q, marking_guide: reconciled }; }
      }
    }
    return q;
  });
  return changed ? next : questions;
}

export { extractMarks, reconcileMarkingGuide, ensureMarkingGuideMarks, reconcileQuestionsForDisplay };
